import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import ModuleBar from 'components/ModuleBar';
import { isMobile } from '@conversion-cloud-widget/UI';
import { initiateWidgetConfigListener, prepareModules } from './App.helpers';
import { PREVIEW } from './constants';

const defaultCollapseThreshhold = 4;

const StyledContainer = styled.div`
    display: flex;
    justify-content: ${props => props.justify};
    position: fixed;
    height: 0;
    ${props => props.yPosition + ':0;'}
    width: 100vw;
    z-index: 999999999999;
`;

const App = ({ widgetConfiguration, reload, ...props }) => {
    let justify;
    const { publicApps } = props;
    const mobile = isMobile();

    const [config, setConfig] = useState(widgetConfiguration);
    const [modules, setModules] = useState([]);

    const {
        company_id,
        company_phone,
        collapse_threshhold = defaultCollapseThreshhold,
        collapse_threshhold_mobile = defaultCollapseThreshhold,
        wallet_greeting_heading,
        wallet_greeting_subheading,
        widget_adjustments,
        widgets_order,
        widgets_order_mobile,
        brand_primary_color = '#0054FE',
        brand_text_color,
        widgetbar_expanded,
        widget_notification_bar_active,
        widget_notification_bar_background_color,
        widget_notification_bar_color,
        widget_notification_bar_links,
        widget_notification_bar_message,
        widget_position,
        widget_position_mobile,
        widget_size = 'large',
        skin,
        widget_spacer_enabled,
        widget_style: widgetMode = 'light',
        widget_animation_style
    } = config;
    useEffect(() => {
        if (widget_spacer_enabled !== reload) {
            window.location.reload();
        }
    }, [widget_spacer_enabled]);

    let moduleCount = modules.length;

    moduleCount =
        publicApps &&
        publicApps.reduce((count, module) => {
            count +=
                module.name === 'MODULE_CHAT'
                    ? 0
                    : module.name === 'MODULE_CONCESSIONMANAGER'
                        ? module.app_data.offer_ids.length
                        : 1;
            return count;
        }, 0);

    const position = mobile ? widget_position_mobile : widget_position;
    const xPosition = position.split('-')[1];
    const yPosition = position.split('-')[0];

    switch (xPosition) {
        case 'left':
            justify = 'flex-start';
            break;
        case 'middle':
            justify = 'center';
            break;
        case 'right':
            justify = 'flex-end';
            break;
    }

    // Apply any necessary adjustments made in the admin UI
    let adjustments = '';
    Object.keys(widget_adjustments).map(adjustment => {
        if (!mobile) {
            if (!adjustment.startsWith('mobile')) {
                adjustments +=
                    widget_adjustments[adjustment] > 0 ? `${adjustment}:${widget_adjustments[adjustment]}px;` : '';
            }
        } else {
            if (adjustment.startsWith('mobile')) {
                adjustments +=
                    widget_adjustments[adjustment] > 0
                        ? `${adjustment.replace('mobile_', '')}:${widget_adjustments[adjustment]}px;`
                        : '';
            }
        }
    });

    const shouldShowNotification = widget_notification_bar_active && widget_notification_bar_message?.length > 0;

    const widgetsOrder = isMobile() ? widgets_order_mobile : widgets_order;
    // Only display the modules that are available in public apps because those are supposed to be the true source of enabled and active apps
    const configuredModules = useMemo(
        () =>
            widgetsOrder.reduce((arr, module) => {
                if (props.publicApps.filter(app => app.name === module).length) {
                    arr.push(module);
                }
                return arr;
            }, []),
        [widgets_order_mobile, widgets_order]
    );

    // Set up modules
    useEffect(async () => {
        const loadedModules = await prepareModules(company_id, configuredModules, shouldShowNotification, {
            backgroundColor: widget_notification_bar_background_color,
            links: widget_notification_bar_links,
            message: widget_notification_bar_message,
            textColor: widget_notification_bar_color
        });
        setModules(loadedModules);
    }, [
        widgetConfiguration.companyId,
        widgetConfiguration.widget_notification_bar_background_color,
        widgetConfiguration.widget_notification_bar_links,
        widgetConfiguration.widget_notification_bar_message,
        widgetConfiguration.widget_notification_bar_color
    ]);

    // have to add this useEffect inorder for it to update the index of app orders
    useEffect(() => {
        if (modules?.length) {
            let updatedPositions = widgetsOrder.map(value =>
                modules.find(module => String(value) === String(module?.name))
            );
            updatedPositions = updatedPositions.filter(item => item !== undefined);
            setModules(updatedPositions);
        }
    }, [widgetsOrder, config]);

    // This subscribes to exceptions from widget modules.
    // We will uncomment this when we re-implement production error logging
    // useEffect(() => {
    //     subscribe(Events.EXCEPTION, payload => {
    //         const { module, companyId, data } = payload;
    //         const { e: error } = data;
    //         process.env.NODE_ENV !== 'development'
    //             ? console.log(`${module} error - Company Id: ${companyId}.`, { error })
    //             : console.log(payload);
    //     });
    // }, []);

    // On initialization set up the widget to listen to iframe messages if in preview mode
    useEffect(() => {
        if (window[PREVIEW]) {
            initiateWidgetConfigListener(updatedWidgetConfig => {
                const newConfig = { ...config, ...updatedWidgetConfig };
                setConfig(newConfig);

                // Note: we set the config to session storage here so the individual launchers are updated with the new config
                sessionStorage.setItem(process.env.TCC_SESSION_STORAGE_WIDGET_CONFIG, JSON.stringify(newConfig));
            });
        }
    }, []);

    // Ensure modules are loaded before loading the ModuleBar
    if (!modules.length) {
        return <></>;
    }

    return (
        <StyledContainer className="clx-container" data-testid="clx-container" justify={justify} yPosition={yPosition}>
            <ModuleBar
                adjustments={adjustments}
                primaryColor={brand_primary_color}
                collapseThreshhold={{ desktop: collapse_threshhold, mobile: collapse_threshhold_mobile }}
                companyId={company_id}
                companyPhone={company_phone}
                mode={widgetMode}
                modules={modules}
                moduleCount={moduleCount}
                size={widget_size}
                skin={skin || config.widget_skin} // Note: skin is being set as widget_skin, so we need to check for both here
                textColor={brand_text_color}
                walletGreeting={{
                    heading: wallet_greeting_heading,
                    subheading: wallet_greeting_subheading
                }}
                widgetExpanded={widgetbar_expanded}
                xPosition={xPosition}
                yPosition={yPosition}
                animation={widget_animation_style}
            />
        </StyledContainer>
    );
};

export default App;
